// API calls are now proxied through the server - no client-side SDK usage
import type { MarketingReport, MediaType, ModelType, InputMode, CampaignType, GeoRegion, AudienceSimulation, AudiencePersona, PersonaReaction, RankedElement } from '../types'
import { marketingReportSchema } from '../types'
import { reportCacheService } from './reportCacheService'

// Attempt to repair truncated JSON by closing unclosed brackets
function repairTruncatedJSON(text: string): string {
  let repaired = text.trim()

  // Count brackets
  let braceCount = 0
  let bracketCount = 0
  let inString = false
  let escape = false

  for (const char of repaired) {
    if (escape) {
      escape = false
      continue
    }
    if (char === '\\') {
      escape = true
      continue
    }
    if (char === '"') {
      inString = !inString
      continue
    }
    if (inString) continue

    if (char === '{') braceCount++
    else if (char === '}') braceCount--
    else if (char === '[') bracketCount++
    else if (char === ']') bracketCount--
  }

  // If we're in an unclosed string, close it
  if (inString) {
    repaired += '"'
  }

  // Close any unclosed brackets
  while (bracketCount > 0) {
    repaired += ']'
    bracketCount--
  }

  // Close any unclosed braces
  while (braceCount > 0) {
    repaired += '}'
    braceCount--
  }

  return repaired
}

// Model configurations are now handled server-side via the proxy
// The frontend just passes model type names (fast, pro, thinking)

// Usage limits interface
export interface UsageLimits {
  allowed: boolean
  remaining: number
  limit: number
  used: number
  modelAccess: string[]
  tier: 'anonymous' | 'tracked' | 'admin'
  hasRef: boolean
}

// Check usage limits from the server
export async function checkUsageLimits(ref?: string | null): Promise<UsageLimits> {
  try {
    const url = ref ? `/api/check-limits?ref=${encodeURIComponent(ref)}` : '/api/check-limits'
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error('Failed to check limits')
    }
    return await response.json()
  } catch (error) {
    console.error('Failed to check usage limits:', error)
    // Default to anonymous limits on error
    return {
      allowed: true,
      remaining: 3,
      limit: 3,
      used: 0,
      modelAccess: ['fast'],
      tier: 'anonymous',
      hasRef: false
    }
  }
}

// API is now proxied through the worker - no client-side keys needed
// Check if API is available via server proxy
export function hasApiKey(): boolean {
  // API is available if the server is configured (always true when deployed)
  return window.RUNTIME_CONFIG?.API_AVAILABLE === true ||
    import.meta.env.DEV // In dev mode, assume it's available
}

export function hasImageApiKey(): boolean {
  // Image API is also proxied through the server
  return hasApiKey()
}

// Get API key source for diagnostics
export function getApiKeySource(): string {
  if (window.RUNTIME_CONFIG?.API_AVAILABLE) {
    return 'Server Proxy (Secure)'
  }
  if (import.meta.env.DEV) {
    return 'Development Mode'
  }
  return 'Not Configured'
}

// Helper to get current ref for rate limiting
function getCurrentRef(): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get('ref')
}

// Build geographic context for prompts
function buildGeoContext(regions: GeoRegion[]): string {
  if (regions.length === 0) return ''

  const regionCodes = regions.join(', ')

  const geoGuidance: Record<GeoRegion, string> = {
    'USCAN': 'US theatrical/streaming release windows, American cultural touchpoints, Super Bowl/major US event tie-ins, English-language marketing.',
    'UK': 'BAFTA awards positioning, UK-specific broadcasters (BBC, ITV, Channel 4, Sky), British cultural references, London premiere events.',
    'EU': 'Multi-language marketing needs, major European film festivals (Cannes, Berlin, Venice), EU streaming platforms, cultural adaptation for diverse markets.',
    'EMEA': 'Broad regional approach covering Western Europe, Middle East, and Africa. Dubai/Abu Dhabi premieres, pan-regional streaming deals, diverse cultural sensitivities.',
    'LATAM': 'Spanish/Portuguese language marketing, Mexico City/São Paulo premiere events, Latin American streaming platforms, regional celebrity partnerships.',
    'ANZ': 'Australian/NZ release timing (opposite hemisphere seasons), local broadcaster partnerships, AACTA awards consideration, regional premiere events.',
    'Japan': 'Japanese language/subtitle requirements, anime/manga crossover potential, Japanese distributor relationships, Tokyo premiere events, local celebrity voice casting.',
  }

  const specificGuidance = regions.map(r => `- ${r}: ${geoGuidance[r]}`).join('\n')

  return `
TARGET GEOGRAPHIC MARKETS: ${regionCodes}

CRITICAL: You must generate SEPARATE marketing plans for EACH region listed above. The "regionalPlans" object must contain a key for each region with its own complete marketing plan tailored to that market.

Regional considerations:
${specificGuidance}

Each regional plan should have localized:
- Ad placements (local platforms, networks, publications)
- Social strategy (region-specific platforms and influencers)
- Cross-promotions (local brand partnerships)
- Stunts/events (local venues and cultural moments)
- Budget (appropriate for that market's size)
- Timeline (accounting for local release windows and cultural events)
`
}

// Build the marketing strategy prompt
function buildPrompt(
  userInput: string,
  mediaType: MediaType,
  inputMode: InputMode,
  campaignType: CampaignType,
  regions: GeoRegion[]
): string {
  const modeContext = inputMode === 'existing'
    ? 'Research this existing title using your knowledge. Find real information about this IP including cast, creators, release dates, and reception.'
    : 'This is a new concept that does not exist yet. Be creative and build a compelling strategy based on the provided logline/summary.'

  const geoContext = buildGeoContext(regions)

  if (campaignType === 'fyc') {
    return buildFYCPrompt(userInput, mediaType, modeContext, geoContext)
  }

  return buildConsumerPrompt(userInput, mediaType, modeContext, geoContext)
}

// Build consumer marketing prompt
function buildConsumerPrompt(
  userInput: string,
  mediaType: MediaType,
  modeContext: string,
  geoContext: string
): string {
  const persona = mediaType === 'tv'
    ? 'You are an experienced TV marketing executive with 20+ years in the television industry. You think in terms of ratings, seasons, episode engagement, and building long-term audience loyalty.'
    : 'You are a seasoned film marketing executive with deep experience in theatrical releases. You think in terms of box office performance, opening weekends, awards potential, and theatrical vs streaming strategies.'

  const metricTerminology = mediaType === 'tv'
    ? 'Use "Ratings" as the performance metric for competitor analysis.'
    : 'Use "Box Office" as the performance metric for competitor analysis.'

  return `${persona}

${modeContext}

${metricTerminology}
${geoContext}
Based on the following ${mediaType === 'tv' ? 'TV show' : 'movie'} information, generate a comprehensive marketing strategy:

${userInput}

Create a detailed marketing report with the following sections:
1. Show/Movie Info - Extract or create title, genre, cast, summary, and relevant metadata
2. Audience Profile - Define primary and secondary demographics, psychographics, and media consumption habits
3. Competitor Analysis - Identify 3 similar titles, their success factors, and lessons to learn
4. Regional Marketing Plans - Generate a SEPARATE marketing plan for EACH target region. Each plan should include region-specific ad placements, social strategy, cross-promotions, stunts/events, budget breakdown, and timeline
5. Key Art Concepts - Create 3 distinct visual directions with detailed prompts for image generation

IMPORTANT: The "regionalPlans" object must have a separate key for each target region (e.g., "USCAN", "UK", etc.) with a complete marketing plan tailored to that specific market.

Be specific with budget numbers (use realistic industry figures for each region), reach estimates, and timing. Each key art concept should have a detailed generative prompt suitable for AI image generation.

Generate unique IDs for each key art concept using the format "keyart_1", "keyart_2", "keyart_3".

IMPORTANT: Respond with ONLY valid JSON matching this schema. Do not include any text before or after the JSON:

${JSON.stringify(marketingReportSchema, null, 2)}`
}

// Build FYC (For Your Consideration) Awards campaign prompt
function buildFYCPrompt(
  userInput: string,
  mediaType: MediaType,
  modeContext: string,
  geoContext: string
): string {
  const awardsBody = mediaType === 'tv' ? 'Emmy Awards and Television Academy' : 'Academy Awards (Oscars) and Academy of Motion Picture Arts and Sciences'
  const categories = mediaType === 'tv'
    ? 'Outstanding Drama Series, Outstanding Limited Series, Outstanding Lead Actor/Actress, Outstanding Supporting Actor/Actress, Outstanding Directing, Outstanding Writing, etc.'
    : 'Best Picture, Best Director, Best Actor, Best Actress, Best Supporting Actor, Best Supporting Actress, Best Original Screenplay, Best Adapted Screenplay, Best Cinematography, Best Film Editing, Best Original Score, Best Visual Effects, etc.'

  return `You are an elite awards campaign strategist with 25+ years of experience running successful FYC (For Your Consideration) campaigns. You have deep relationships with Academy/Television Academy members and understand exactly how to position content for awards recognition.

${modeContext}
${geoContext}
Your expertise includes:
- Understanding the voting blocs within the ${awardsBody}
- Crafting compelling narratives that resonate with specific Academy branches
- Planning exclusive screenings in New York and Los Angeles
- Developing targeted messaging for different awards categories
- Managing guild relationships and screenings
- Trade publication advertising strategies (Variety, Hollywood Reporter, Deadline)

Based on the following ${mediaType === 'tv' ? 'TV show' : 'movie'} information, generate a comprehensive FYC Awards campaign strategy:

${userInput}

Create a detailed FYC campaign report with the following sections:

1. Show/Movie Info - Extract or create title, genre, cast, summary, and relevant metadata

2. Audience Profile - For FYC, focus on Academy/Guild voter demographics. Consider:
   - Branch breakdown (Actors, Directors, Writers, Producers, etc.)
   - Geographic distribution (LA-heavy, some NYC)
   - Viewing preferences and habits
   - What resonates with different voter segments

3. Competitor Analysis - Identify 3 titles competing for the same awards categories:
   - Their awards positioning
   - Campaign strengths/weaknesses
   - How to differentiate

4. Regional Marketing Plans - Generate a SEPARATE marketing plan for EACH target region in the "regionalPlans" object. For FYC campaigns, focus on:
   - Trade publication ads (Variety, THR, Deadline for US; Screen International for UK/EU)
   - Digital campaigns targeting industry professionals in each region
   - Timeline aligned with awards season calendar (Oscars for US, BAFTAs for UK, etc.)

5. Key Art Concepts - Create 3 FYC-specific visual directions:
   - Sophisticated, awards-worthy aesthetic
   - Category-specific variations if needed
   - "For Your Consideration" branding

6. FYC Strategy (CRITICAL - include this section):
   - overallNarrative: The central story/angle for the awards campaign
   - primaryCategories: Array of recommended submission categories, each with:
     * category: Specific award category name (e.g., "${categories}")
     * likelihood: "high", "medium", or "low"
     * reasoning: Why this category is appropriate
     * competingTitles: Other titles competing in this category
     * campaignAngle: How to position for this specific category
     * voterMessaging: Specific messaging for voters in this category's branch
   - screenings: Array of exclusive screenings, each with:
     * name: Event name (e.g., "Academy Members Screening")
     * location: "New York", "Los Angeles", or "Other"
     * venue: Specific venue (e.g., "DGA Theater", "Academy Museum")
     * date: Proposed date during awards season
     * description: What makes this screening special
     * targetAttendees: Who should attend (e.g., "Actors Branch members")
     * talentAttending: Which cast/crew will be present
     * qAndA: Whether there's a Q&A
     * budget: Event budget
   - voterOutreach: Array of branch-specific outreach strategies:
     * branch: Academy/Guild branch name
     * strategy: How to reach these voters
     * touchpoints: List of contact points
     * keyMessages: What to emphasize for this branch
     * materialsNeeded: Screeners, mailers, etc.
   - tradeAdPlacements: Array of trade publication ads:
     * publication: Variety, THR, Deadline, etc.
     * format: Full page, spread, digital, etc.
     * timing: When during awards season
     * budget: Ad spend
     * messaging: Key copy points
   - guildScreenings: Array of guild-specific screenings:
     * guild: SAG, DGA, WGA, PGA, etc.
     * date: When
     * location: Where
     * strategy: Approach for this guild
   - totalBudget: Overall FYC campaign budget
   - budgetBreakdown: How budget is allocated
   - timeline: Phased approach through awards season

Be specific with:
- Realistic FYC budget figures ($500K-$20M+ depending on scope)
- Actual venue names in NY/LA
- Real trade publications and their rates
- Awards calendar timing (nominations, voting periods)
- Branch-specific strategies

Generate unique IDs for each key art concept using the format "keyart_1", "keyart_2", "keyart_3".

IMPORTANT: Respond with ONLY valid JSON matching the schema. Include the fycStrategy object. Do not include any text before or after the JSON:

${JSON.stringify(marketingReportSchema, null, 2)}`
}

// Build chat system instruction
function buildChatSystemInstruction(report: MarketingReport): string {
  const persona = report.mediaType === 'tv'
    ? 'You are the TV marketing strategist who created this marketing plan.'
    : 'You are the film marketing strategist who created this marketing plan.'

  return `${persona}

You have deep knowledge of this marketing strategy and can answer questions, provide clarifications, and suggest refinements.

Here is the complete marketing report you created:

${JSON.stringify(report, null, 2)}

Maintain your expert persona throughout the conversation. Provide actionable, specific advice. Reference the data from the report when relevant. If asked to modify the strategy, explain your reasoning based on industry best practices.`
}

// Main generation function
export async function generateMarketingStrategy(
  userInput: string,
  mediaType: MediaType,
  modelType: ModelType,
  inputMode: InputMode,
  campaignType: CampaignType = 'consumer',
  regions: GeoRegion[] = ['USCAN'],
  skipCache: boolean = false
): Promise<{ report: MarketingReport; chatSession: ChatSession; fromCache: boolean }> {
  // Check cache first (unless skipCache is true)
  const cacheParams = { userInput, mediaType, inputMode, campaignType, regions }
  if (!skipCache) {
    const cachedReport = reportCacheService.getCachedReport(cacheParams)
    if (cachedReport) {
      console.log('Returning cached report')
      const chatSession = createChatSession(cachedReport)
      return { report: cachedReport, chatSession, fromCache: true }
    }
  }

  const prompt = buildPrompt(userInput, mediaType, inputMode, campaignType, regions)

  // Configure based on model type - use higher token limit for complete responses
  const maxTokens = modelType === 'thinking' ? 32000 : 16000

  // Build request for proxy endpoint
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestBody: any = {
    model: modelType, // Use model type name, server will map to actual model
    max_tokens: maxTokens,
    system: 'You are an entertainment marketing expert. You MUST output valid JSON only. No markdown code blocks, no explanation, no preamble. Start with { and end with }. Your response must be directly parseable by JSON.parse().',
    messages: [
      {
        role: 'user',
        content: prompt,
      },
    ],
    ref: getCurrentRef(), // For rate limiting
  }

  // Add adaptive thinking for thinking mode
  if (modelType === 'thinking') {
    requestBody.thinking = {
      type: 'adaptive',
    }
  }

  // Call the server proxy instead of Gemini directly
  const proxyResponse = await fetch('/api/ai/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  })

  if (!proxyResponse.ok) {
    const errorData = await proxyResponse.json().catch(() => ({}))
    throw new Error(errorData.error || `API request failed: ${proxyResponse.status}`)
  }

  const response = await proxyResponse.json()

  // Extract text from response - with structured output, this should be valid JSON
  let responseText = ''
  for (const block of response.content) {
    if (block.type === 'text') {
      responseText += block.text
      break
    }
  }

  // Parse the response - try multiple strategies
  let reportData: Partial<MarketingReport> | undefined
  let cleanedText = responseText.trim()

  // Remove any trailing content after the last }
  const lastBrace = cleanedText.lastIndexOf('}')
  if (lastBrace !== -1 && lastBrace < cleanedText.length - 1) {
    cleanedText = cleanedText.substring(0, lastBrace + 1)
  }

  // Strategy 1: Direct parse
  try {
    reportData = JSON.parse(cleanedText)
  } catch {
    // Continue to next strategy
  }

  // Strategy 2: Repair truncated JSON
  if (!reportData) {
    try {
      const repairedText = repairTruncatedJSON(cleanedText)
      reportData = JSON.parse(repairedText)
      console.log('JSON repair successful')
    } catch {
      // Continue to next strategy
    }
  }

  // Strategy 3: Extract from markdown code blocks
  if (!reportData) {
    const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
    if (jsonMatch) {
      try {
        const repairedBlock = repairTruncatedJSON(jsonMatch[1].trim())
        reportData = JSON.parse(repairedBlock)
        console.log('Extracted JSON from code block')
      } catch {
        // Continue to next strategy
      }
    }
  }

  // Strategy 4: Find JSON object in response
  if (!reportData) {
    const jsonStart = responseText.indexOf('{')
    if (jsonStart !== -1) {
      try {
        const jsonText = responseText.slice(jsonStart)
        const repairedJson = repairTruncatedJSON(jsonText)
        reportData = JSON.parse(repairedJson)
        console.log('JSON extraction and repair successful')
      } catch {
        // All strategies failed
      }
    }
  }

  // If all strategies failed, throw error
  if (!reportData) {
    console.error('All JSON parsing strategies failed')
    console.error('Response length:', responseText.length)
    console.error('First 500 chars:', responseText.substring(0, 500))
    console.error('Last 500 chars:', responseText.substring(Math.max(0, responseText.length - 500)))
    throw new Error('Failed to parse AI response as JSON')
  }

  // Validate regional plans - check if all selected regions have plans
  const generatedRegions = Object.keys(reportData.regionalPlans || {})
  const missingRegions = regions.filter(r => !generatedRegions.includes(r))
  if (missingRegions.length > 0) {
    console.warn(`Missing regional plans for: ${missingRegions.join(', ')}`)
    console.warn(`Generated plans for: ${generatedRegions.join(', ')}`)
    console.warn('This may be due to token limits. Try selecting fewer regions or using the Pro model.')
  }

  // Build complete report
  const report: MarketingReport = {
    id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    mediaType,
    inputMode,
    modelUsed: modelType,
    campaignType,
    selectedRegions: regions,
    showInfo: reportData.showInfo!,
    audienceProfile: reportData.audienceProfile!,
    competitorAnalysis: reportData.competitorAnalysis!,
    regionalPlans: reportData.regionalPlans || {},
    keyArtConcepts: reportData.keyArtConcepts!,
    fycStrategy: reportData.fycStrategy,
    createdAt: new Date().toISOString(),
    userInput,
  }

  // Cache the generated report
  reportCacheService.cacheReport(cacheParams, report)

  // Create chat session
  const chatSession = createChatSession(report)

  return { report, chatSession, fromCache: false }
}

// Chat Session class
export class ChatSession {
  private report: MarketingReport
  private systemInstruction: string
  private conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>

  constructor(report: MarketingReport, systemInstruction: string) {
    this.report = report
    this.systemInstruction = systemInstruction
    this.conversationHistory = []
  }

  async sendMessage(message: string): Promise<string> {
    // Add user message to history
    this.conversationHistory.push({ role: 'user', content: message })

    // Call the server proxy instead of Gemini directly
    const proxyResponse = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: this.systemInstruction,
        messages: this.conversationHistory,
      }),
    })

    if (!proxyResponse.ok) {
      const errorData = await proxyResponse.json().catch(() => ({}))
      throw new Error(errorData.error || 'Chat request failed')
    }

    const response = await proxyResponse.json()

    // Extract text from response
    let responseText = ''
    for (const block of response.content) {
      if (block.type === 'text') {
        responseText = block.text
        break
      }
    }

    // Add assistant response to history
    this.conversationHistory.push({ role: 'assistant', content: responseText })

    return responseText || 'No response received.'
  }

  getReport(): MarketingReport {
    return this.report
  }
}

// Create a chat session with report context
export function createChatSession(report: MarketingReport): ChatSession {
  const systemInstruction = buildChatSystemInstruction(report)
  return new ChatSession(report, systemInstruction)
}

// Build audience simulation prompt
function buildSimulationPrompt(report: MarketingReport): string {
  // Get the first available regional plan for simulation
  const regions = Object.keys(report.regionalPlans) as (keyof typeof report.regionalPlans)[]
  const firstPlan = regions.length > 0 ? report.regionalPlans[regions[0]] : null

  // Limit campaign elements to prevent response from being too large
  const allElements = firstPlan ? [
    ...firstPlan.adPlacements.slice(0, 2).map(ad => ({
      type: 'ad',
      name: `${ad.platform} - ${ad.format}`,
    })),
    ...firstPlan.socialStrategy.slice(0, 2).map(social => ({
      type: 'social',
      name: `${social.platform} Strategy`,
    })),
    ...firstPlan.stuntsEvents.slice(0, 1).map(event => ({
      type: 'event',
      name: event.name,
    })),
    ...report.keyArtConcepts.slice(0, 2).map(art => ({
      type: 'keyart',
      name: art.name,
    })),
  ] : [
    ...report.keyArtConcepts.slice(0, 3).map(art => ({
      type: 'keyart',
      name: art.name,
    })),
  ]

  const campaignElements = allElements.slice(0, 6)

  return `Create an audience simulation for "${report.showInfo.title}" (${report.showInfo.genre}).

TARGET AUDIENCE: Ages ${report.audienceProfile.primaryAge.min}-${report.audienceProfile.primaryAge.max}, interests: ${report.audienceProfile.interests.slice(0, 3).join(', ')}

CAMPAIGN ELEMENTS:
${campaignElements.map((el, i) => `${i + 1}. [${el.type}] ${el.name}`).join('\n')}

Generate JSON with this EXACT structure (3 personas, keep responses concise):
{
  "personas": [
    {"id": "persona_1", "name": "string", "age": number, "gender": "string", "occupation": "string", "location": "string", "income": "low|middle|high", "interests": ["string"], "mediaHabits": ["string"], "psychographicProfile": "string", "avatar": "emoji"}
  ],
  "reactions": [
    {"personaId": "persona_1", "overallResonance": number, "elementReactions": [{"element": "string", "elementType": "ad|social|event|keyart", "resonanceScore": number, "emotionalResponse": "string", "reasoning": "string", "wouldShare": boolean, "wouldEngage": boolean}], "topStrengths": ["string"], "topConcerns": ["string"], "refinementSuggestions": ["string"], "likelyToWatch": "definitely|probably|maybe|unlikely|no"}
  ],
  "rankedElements": [
    {"element": "string", "elementType": "string", "averageScore": number, "personaScores": [{"personaId": "string", "score": number}], "strengths": ["string"], "weaknesses": ["string"]}
  ],
  "overallScore": number,
  "summary": "string",
  "topRecommendations": ["string"]
}`
}

// Generate audience simulation
export async function generateAudienceSimulation(
  report: MarketingReport
): Promise<AudienceSimulation> {
  const prompt = buildSimulationPrompt(report)

  // Call the server proxy instead of Gemini directly
  const proxyResponse = await fetch('/api/ai/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'fast',
      max_tokens: 16000,
      system: 'You output valid JSON only. No markdown, no explanation, no preamble. Your response must be parseable by JSON.parse(). Do not wrap in code fences.',
      messages: [
        {
          role: 'user',
          content: prompt,
        },
      ],
      ref: getCurrentRef(),
    }),
  })

  if (!proxyResponse.ok) {
    const errorData = await proxyResponse.json().catch(() => ({}))
    throw new Error(errorData.error || 'Simulation request failed')
  }

  const response = await proxyResponse.json()

  // Extract text from response
  let responseText = ''
  for (const block of response.content) {
    if (block.type === 'text') {
      responseText += block.text
      break
    }
  }

  // Parse the response
  let simulationData: {
    personas: AudiencePersona[]
    reactions: PersonaReaction[]
    rankedElements: RankedElement[]
    overallScore: number
    summary: string
    topRecommendations: string[]
  }

  // Clean the response text - strip markdown code fences if present
  responseText = responseText.trim()
  if (responseText.startsWith('```')) {
    responseText = responseText.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '')
  }

  // Extract JSON object from first { to last }
  const firstBrace = responseText.indexOf('{')
  const lastBrace = responseText.lastIndexOf('}')
  if (firstBrace !== -1 && lastBrace !== -1) {
    responseText = responseText.substring(firstBrace, lastBrace + 1)
  }

  try {
    simulationData = JSON.parse(responseText)
  } catch (parseError) {
    console.error('JSON parse error:', parseError)
    console.error('Response length:', responseText.length)
    console.error('First 500 chars:', responseText.substring(0, 500))
    console.error('Last 500 chars:', responseText.substring(Math.max(0, responseText.length - 500)))
    throw new Error('Failed to parse simulation response. Check console for details.')
  }

  // Validate required fields exist
  if (!simulationData.personas || !simulationData.reactions || !simulationData.rankedElements) {
    console.error('Missing required fields in simulation data:', Object.keys(simulationData))
    throw new Error('Simulation response is missing required fields')
  }

  // Build complete simulation
  const simulation: AudienceSimulation = {
    id: `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    reportId: report.id,
    personas: simulationData.personas,
    reactions: simulationData.reactions,
    rankedElements: simulationData.rankedElements,
    overallScore: simulationData.overallScore || 5,
    summary: simulationData.summary || 'Simulation completed.',
    topRecommendations: simulationData.topRecommendations || [],
    createdAt: new Date().toISOString(),
  }

  return simulation
}

// Generate key art image (uses Gemini via server proxy)
export async function generateKeyArt(prompt: string): Promise<string> {
  const enhancedPrompt = `Cinematic 16:9 aspect ratio key art poster concept. Professional movie/TV show marketing material. High quality, dramatic lighting, compelling composition. ${prompt}`

  // Call the server proxy instead of Gemini directly
  const proxyResponse = await fetch('/api/ai/image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt: enhancedPrompt }),
  })

  if (!proxyResponse.ok) {
    const errorData = await proxyResponse.json().catch(() => ({}))
    throw new Error(errorData.error || 'Image generation failed')
  }

  const response = await proxyResponse.json()

  // Check if our server proxy cached this in R2 and returned a fast delivery URL
  if (response.savedImageUrl) {
    return response.savedImageUrl
  }

  // Extract image from response directly (fallback)
  if (response.candidates?.[0]?.content?.parts) {
    for (const part of response.candidates[0].content.parts) {
      if (part.inlineData?.mimeType?.startsWith('image/')) {
        return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`
      }
    }
  }

  throw new Error('No image generated in response')
}
